import React, { useState, useEffect } from 'react';
import { useBackupPolling } from '../../hooks/useBackupPolling';
import { Star, User, MapPin, Calendar, ArrowLeft, Filter } from 'lucide-react';
import './RatingsDashboard.css';

const RatingsDashboard = ({ onClose }) => {
  const [ratings, setRatings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRatingFilter, setSelectedRatingFilter] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const getBusinessId = () => {
    return localStorage.getItem('businessId') || 
           localStorage.getItem('userBusinessId') || 
           new URLSearchParams(window.location.search).get('businessId');
  };

  const businessId = getBusinessId();

  useEffect(() => {
    fetchRatings();
  }, []);

  // Ratings arrive from customers a few times an hour at most, and there is no
  // socket behind this screen — a 30s refresh running for as long as the page was
  // left open was pure Neon compute time. Refresh quietly while the screen is in
  // use, stop when it is not, and catch up the moment it is looked at again.
  useBackupPolling(() => fetchRatings({ silent: true }), 120000, {
    enabled: Boolean(businessId)
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && !event.target.closest('.filter-btn') && !event.target.closest('[data-filter-dropdown]')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  // `silent` skips the spinner so a background refresh does not blank the list
  // out from under someone reading it.
  const fetchRatings = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      // Use deployqrinv backend URL (same backend as Owner Dashboard)
      const BACKEND_URL = process.env.REACT_APP_API_URL;
      
      
      const response = await fetch(
        `${BACKEND_URL}/ratings/${businessId}?limit=50`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        
        // Try to parse as JSON to get debug details
        try {
          const errorJson = JSON.parse(errorText);
          console.error('❌ API Error Details:', errorJson.debug);
          console.error('🔍 Debug Info:', {
            message: errorJson.debug?.message,
            code: errorJson.debug?.code,
            detail: errorJson.debug?.detail,
            hint: errorJson.debug?.hint
          });
        } catch (e) {
          // Not JSON, just log the text
        }
        
        throw new Error('Failed to fetch ratings');
      }

      const data = await response.json();
      setRatings(data.ratings || []);
      setStatistics(data.statistics || {});
      setError(null);
    } catch (err) {
      console.error('❌ Error fetching ratings:', err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="star-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            fill={star <= rating ? '#F9C806' : 'none'}
            stroke={star <= rating ? '#F9C806' : '#666'}
          />
        ))}
      </div>
    );
  };

  const toggleCardExpansion = (ratingId) => {
    setExpandedCards(prev => ({
      ...prev,
      [ratingId]: !prev[ratingId]
    }));
  };

  const hasReviews = (rating) => {
    return rating.feedback_reason || rating.comment;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    // Parse timestamp properly - backend sends ISO strings
    const date = new Date(dateString);
    const now = new Date();
    const nowTimestamp = now.getTime();
    const dateTimestamp = date.getTime();
    
    // Calculate difference in milliseconds
    const diffMs = nowTimestamp - dateTimestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1m ago';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours === 1) return '1h ago';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // For older dates, show formatted date
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (error && ratings.length === 0) {
    return (
      <div className="ratings-dashboard">
        <div className="ratings-header">
          <h1 className="ratings-title">CUSTOMER RATINGS</h1>
        </div>
        <div className="ratings-error">
          <p>Failed to load ratings: {error}</p>
          <button onClick={fetchRatings} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  const avgRating = statistics?.average_rating ? parseFloat(statistics.average_rating).toFixed(1) : '0.0';
  const totalRatings = statistics?.total_ratings || 0;

  // Filter ratings based on selected rating
  const filteredRatings = selectedRatingFilter === 'all' 
    ? ratings 
    : ratings.filter(r => r.rating === parseInt(selectedRatingFilter));

  if (loading && ratings.length === 0) {
    return (
      <div className="ratings-dashboard">
        <div className="ratings-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
            <button 
              onClick={onClose} 
              className="ratings-back-btn" 
              title="Back"
              >
              <ArrowLeft size={24} />
            </button>
            <h1 className="ratings-title">Customer Ratings</h1>
          </div>
        </div>
        <div className="ratings-loading">
          <div className="ratings-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="ratings-dashboard">
      {/* Header */}
      <div className="ratings-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
          <button 
            onClick={onClose} 
            className="ratings-back-btn" 
            title="Back"
            >
            <ArrowLeft size={24} />
          </button>
          <h1 className="ratings-title">Customer Ratings</h1>
        </div>
        <div className="ratings-filter-container">
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="ratings-filter-btn"
          >
            <Filter size={16} />
            <span>{selectedRatingFilter === 'all' ? 'Filter' : `${selectedRatingFilter} Star${selectedRatingFilter === '1' ? '' : 's'}`}</span>
          </button>

          {/* Filter Dropdown */}
          {showFilterDropdown && (
            <div className="ratings-filter-dropdown" data-filter-dropdown>
              <div
               className={`ratings-filter-option ${selectedRatingFilter === 'all' ? 'active' : ''}`}
                onClick={() => {
                  setSelectedRatingFilter('all');
                  setShowFilterDropdown(false);
                }}
              >
                All Ratings
              </div>
              {[5, 4, 3, 2, 1].map((rating) => (
                <div
                  key={rating}
                  className={`ratings-filter-option ${selectedRatingFilter === rating.toString() ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedRatingFilter(rating.toString());
                    setShowFilterDropdown(false);
                  }}
                >
                   <div className="ratings-filter-stars">
                    {[...Array(rating)].map((_, i) => (
                      <Star key={i} size={14} fill="#F9C806" stroke="#F9C806" />
                    ))}
                  </div>
                  <span>{rating} Star{rating === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards - Removed */}

      {/* Ratings List */}
      <div className="ratings-list-container">
        <div className="ratings-list-header">
          <h2>Recent Ratings</h2>
          <span className="ratings-count">
            {filteredRatings.length} {selectedRatingFilter !== 'all' ? `${selectedRatingFilter} star ` : ''}rating{filteredRatings.length !== 1 ? 's' : ''}
          </span>
        </div>

        {filteredRatings.length === 0 ? (
          <div className="no-ratings">
            <Star size={64} stroke="#666" fill="none" />
            <h3>{selectedRatingFilter !== 'all' ? `No ${selectedRatingFilter} Star Ratings` : 'No Ratings Yet'}</h3>
            <p>{selectedRatingFilter !== 'all' ? 'No ratings found with this filter. Try selecting a different rating filter.' : 'Customer ratings will appear here once they start rating their experience'}</p>
          </div>
        ) : (
          <div className="ratings-list">
            {filteredRatings.map((rating, index) => {
              const uniqueId = rating.id || `rating-${index}`;
              const isExpanded = expandedCards[uniqueId];
              const showReviews = hasReviews(rating);
              
              return (
              <div key={uniqueId} className="rating-card">
                <div className="rating-customer-icon">
                  <User size={20} />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div className="rating-card-header">
                    <div className="rating-customer-info">
                      <div className="rating-customer-details">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <div className="rating-customer-name">{rating.customer_name || 'Anonymous'}</div>
                          <div className="rating-time-inline">{formatDate(rating.created_at)}</div>
                        </div>
                        {rating.customer_phone && (
                          <div className="rating-customer-phone">{rating.customer_phone}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rating-card-body">
                    <div className="rating-stars-large">
                      {renderStars(rating.rating)}
                      <span className="rating-value">{rating.rating}.0</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <div className="rating-meta-info">
                        {rating.table_number && (
                          <div className="rating-meta-item">
                            <MapPin size={16} />
                            <span>Table {rating.table_number}</span>
                          </div>
                        )}
                        {rating.order_number && (
                          <div className="rating-meta-item">
                            <Calendar size={16} />
                            <span>Order #{rating.order_number}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* View All button for cards with reviews */}
                      {showReviews && (
                        <button 
                          className="view-all-btn"
                          onClick={() => toggleCardExpansion(uniqueId)}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      )}
                    </div>

                    {/* Display feedback reason if present and expanded */}
                    {isExpanded && rating.feedback_reason && (
                      <div className="rating-feedback-reason" style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        letterSpacing: '0.2px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '6px' }}>Feedback</div>
                        <div style={{ fontSize: '13px' }}>{rating.feedback_reason}</div>
                      </div>
                    )}

                    {/* Display custom comment if present, expanded, and different from feedback_reason */}
                    {isExpanded && rating.comment && rating.comment !== rating.feedback_reason && (
                      <div className="rating-comment" style={{
                        marginTop: '12px',
                        padding: '12px 14px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        fontStyle: 'italic'
                      }}>
                        "{rating.comment}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RatingsDashboard;
